import { yupResolver } from "@hookform/resolvers/yup";
import { useSubscriptionConfig } from "context/SubscriptionConfigContext";
import { useSubscription } from "context/SubscriptionContext";
import { useSubscriptionPricing } from "context/SubscriptionPricingContext";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { getStepSchema } from "schemas/subscriptionValidationSchema";

export const useSubscriptionForm = () => {
  const {
    subscriptionType,
    moduleType,
    duration,
    country,
    step
  } = useSubscription();

  const { pricingStrategy, getGlobalPricing, getAllOverrides } = useSubscriptionPricing();
  const { config } = useSubscriptionConfig();

  const [currentStepSchema, setCurrentStepSchema] = useState(getStepSchema(0));

  // Initialize React Hook Form
  const methods = useForm({
    resolver: yupResolver(currentStepSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    context: { subscriptionType },
    defaultValues: {
      subscriptionType: subscriptionType || '',
      moduleType: moduleType || '',
      duration: duration || '',
      country: country || '',
      pricingStrategy: {
        strategy: pricingStrategy?.strategy || '',
        moduleType: pricingStrategy?.moduleType || ''
      },
      globalPricing: {
        discountPercentage: 0
      },
      customPricing: {
        selectedEntityIds: [],
        customDiscount: 0,
        discounts: {} // Store discounts by entity ID
      },
      config: {
        coolingOffPeriod: {
          enabled: true,
          days: 14,
          refundPercentage: 100
        },
        renewal: {
          autoRenew: true,
          gracePeriodDays: 7,
          reminderDaysBefore: 3
        },
        cancellation: {
          allowed: false,
          allowedWithinDays: 30,
          refundPercentage: 0,
          adminApprovalRequired: false
        },
        billing: {
          proRataRefund: false,
          billingCycles: 0,
          trialPeriodDays: 0
        }
      }
    }
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid, isSubmitting },
    reset,
    getValues
  } = methods;

  // Update schema when step changes
  useEffect(() => {
    const newSchema = getStepSchema(step);
    setCurrentStepSchema(newSchema);
  }, [step]);

  // When all context values are populated (edit mode), reset form defaults so
  // validation schema picks them up correctly
  useEffect(() => {
    if (subscriptionType && moduleType) {
      methods.reset({
        ...methods.getValues(),
        subscriptionType,
        moduleType,
        duration: duration || '',
        country: country || '',
      }, { keepErrors: false, keepDirty: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionType, moduleType]);

  // When context values are populated (edit mode), reset form so validation sees correct values
  useEffect(() => {
    if (subscriptionType && moduleType) {
      methods.reset({
        ...methods.getValues(),
        subscriptionType,
        moduleType,
        duration: duration || '',
        country: country || '',
      }, { keepErrors: false, keepDirty: false });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionType, moduleType]);

  // Sync form values with contexts
  useEffect(() => {
    if (subscriptionType) setValue('subscriptionType', subscriptionType);
  }, [subscriptionType, setValue]);

  useEffect(() => {
    if (moduleType) setValue('moduleType', moduleType);
  }, [moduleType, setValue]);

  useEffect(() => {
    if (duration) setValue('duration', duration);
  }, [duration, setValue]);

  useEffect(() => {
    if (country) setValue('country', country);
  }, [country, setValue]);

  useEffect(() => {
    if (pricingStrategy?.strategy) {
      setValue('pricingStrategy.strategy', pricingStrategy.strategy);
      setValue('pricingStrategy.moduleType', pricingStrategy.moduleType);
    }
  }, [pricingStrategy, setValue]);

  useEffect(() => {
    if (config) {
      setValue('config', config);
    }
  }, [config, setValue]);

  // Load custom pricing data
  useEffect(() => {
    if (pricingStrategy?.strategy === 'custom') {
      const overrides = getAllOverrides(subscriptionType);
      const entityIds = Object.keys(overrides);
      
      if (entityIds.length > 0) {
        setValue('customPricing.selectedEntityIds', entityIds);
        
        // Set custom discount (use first entity's discount as reference)
        const firstDiscount = overrides[entityIds[0]]?.discountPercentage || 0;
        setValue('customPricing.customDiscount', firstDiscount);
        
        // Set discounts by entity ID
        entityIds.forEach(entityId => {
          setValue(`customPricing.discounts.${entityId}`, overrides[entityId]?.discountPercentage || 0);
        });
      }
    }
  }, [pricingStrategy?.strategy, subscriptionType, getAllOverrides, setValue]);

  // Validate current step
  const validateCurrentStep = useCallback(async () => {
    const stepSchema = getStepSchema(step);
    const isStepValid = await trigger(Object.keys(stepSchema.fields));
    return isStepValid;
  }, [step, trigger]);

  const getFormData = useCallback(() => {
    const values = getValues();
    
    // console.log("📝 Raw form values:", values);
    
    // BASE STRUCTURE
    const formData = {
      subscriptionType: values.subscriptionType,
      moduleType: values.moduleType,
      duration: values.duration,
      country: values.country,
      pricing: {
        strategy: values.pricingStrategy?.strategy || 'global'
      },
      config: values.config || {
        coolingOffPeriod: { enabled: true, days: 14, refundPercentage: 100 },
        renewal: { autoRenew: true, gracePeriodDays: 7, reminderDaysBefore: 3 },
        cancellation: { allowed: false, allowedWithinDays: 30, refundPercentage: 0, adminApprovalRequired: false },
        billing: { proRataRefund: false, billingCycles: 0, trialPeriodDays: 0 }
      }
    };

    // Add pricing details based on strategy
    if (values.pricingStrategy?.strategy === 'global') {
      const globalPricing = getGlobalPricing(subscriptionType);
      formData.pricing.globalDiscount = globalPricing?.discountPercentage || 0;
    }

    if (values.pricingStrategy?.strategy === 'custom') {
      const overrides = getAllOverrides(subscriptionType);
      const entityIds = Object.keys(overrides);
      
      // console.log("🔍 Custom overrides found:", overrides);
      
      if (entityIds.length > 0) {
        // Create array of entity overrides with COMPLETE DETAILS
        const entityOverrides = entityIds.map(entityId => {
          const override = overrides[entityId];
          return {
            entityId: entityId,
            entityName: override?.entityName || entityId,
            entityType: override?.entityType || moduleType,
            discountPercentage: override?.discountPercentage || 0
          };
        });
        
        formData.pricing.entityOverrides = entityOverrides;
      } else {
        formData.pricing.entityOverrides = [];
      }
      
      // For custom strategy, globalDiscount should be 0
      formData.pricing.globalDiscount = 0;
    }

    // console.log("🚀 Final form data for API:", JSON.stringify(formData, null, 2));
    return formData;
  }, [getValues, getGlobalPricing, getAllOverrides, subscriptionType, moduleType]);

  const resetForm = useCallback(() => {
    reset();
  }, [reset]);

  return {
    // Form methods
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset: resetForm,
    getValues,
    
    // Form state
    errors,
    isValid,
    isSubmitting,
    
    // Custom methods
    validateCurrentStep,
    getFormData,
    
    methods
  };
};
